'use client'

import { PremiumChapterGate } from '@/components/stories/premium-chapter-gate'
import { StoryContinuationPanel } from '@/components/stories/story-continuation-panel'
import { Button } from '@/components/ui/button'
import type {
  StoryChapterNavigation,
  StoryData,
  StoryLearningIndexItem
} from '@/content/stories'
import { useRouter } from '@/i18n/routing'
import { storyToReaderContent } from '@/lib/reader/story-adapter'
import Image from 'next/image'
import { ReaderShell } from './reader-shell'

function StoryCoverMedia({ story }: { story: StoryData }) {
  if (!story.coverImage) return null

  return (
    <figure className="bg-muted relative h-[clamp(9rem,28dvh,18rem)] w-full max-w-2xl overflow-hidden rounded-lg border shadow-sm sm:h-[clamp(12rem,30dvh,20rem)] md:h-[22rem]">
      <Image
        src={story.coverImage.src}
        alt={story.coverImage.alt}
        fill
        priority
        sizes="(min-width: 1024px) 672px, (min-width: 640px) 640px, 100vw"
        className="object-cover"
      />
    </figure>
  )
}

export function StoryReaderRouteClient({
  story,
  storyNavigation,
  storyLearningIndex,
  backHref = '/read',
  surface = 'app-read-story',
  useRouterBack = true,
  className
}: {
  story: StoryData
  storyNavigation?: StoryChapterNavigation | null
  storyLearningIndex?: StoryLearningIndexItem[]
  backHref?: string
  surface?: 'app-read-story' | 'public-read-story'
  useRouterBack?: boolean
  className?: string
}) {
  const router = useRouter()
  const content = storyToReaderContent(story)

  return (
    <PremiumChapterGate story={story} surface={surface}>
      <ReaderShell
        content={content}
        backHref={backHref}
        onBack={useRouterBack ? () => router.push(backHref) : undefined}
        mediaSlot={
          story.coverImage ? <StoryCoverMedia story={story} /> : undefined
        }
        scrollMode="page"
        sideSlot={
          <StoryContinuationPanel
            navigation={storyNavigation ?? null}
            storyLearningIndex={storyLearningIndex ?? []}
            currentSlug={story.slug}
            currentTitle={story.title_en}
            variant="side"
          />
        }
        className={className ?? 'min-h-dvh'}
      />
      <StoryContinuationPanel
        navigation={storyNavigation ?? null}
        storyLearningIndex={storyLearningIndex ?? []}
        currentSlug={story.slug}
        currentTitle={story.title_en}
        variant="below"
        className="lg:hidden"
      />
    </PremiumChapterGate>
  )
}

export function StoryNotFound() {
  const router = useRouter()

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-destructive text-sm">Story not found.</p>
      <Button onClick={() => router.push('/read')}>Back to reader</Button>
    </div>
  )
}
